import React, { useState } from 'react';
import moment from 'moment';
import { toast } from 'react-toastify';
import { Button, Row, Col } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { projectService } from '../../services';
import Loading from '../Loader';
import { convertSecondsToHoursAndMinutes } from '../../helpers';
import { NO_DATA } from '../../constants';
import { DeleteProjectButton } from '.';

const pageLimit = 10;

const ArchivedProject = () => {
  const permissions = useSelector((reducer) => reducer.permissionsReducer);
  const [archivedProjects, setArchivedProjects] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoading, setLoading] = useState(true);
  const [loadMore, setLoadMore] = useState(true);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const getArchivedProjects = () => {
    setLoading(true);
    const params = {
      teamOwnerId: localStorage.getItem('teamOwnerId'),
      page: currentPage,
      limit: pageLimit,
      summaryList: false,
      filter: '{"projectStatus":"close"}',
    };

    projectService
      .get_projects_data(params)
      .then((response) => {
        if (response.data) {
          const { metadata, projects } = response.data;
          setArchivedProjects([...archivedProjects, ...projects]);
          setLoadMore(metadata.total / pageLimit > metadata.page);
          const totalPages = Math.ceil(metadata.total / pageLimit);
          if (currentPage < totalPages) {
            setCurrentPage((prev) => prev + 1);
          }
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const dataFetcher = React.useRef(getArchivedProjects);
  const observer = React.useRef(
    new IntersectionObserver(
      (entries) => {
        const first = entries[0];
        if (first.isIntersecting) {
          dataFetcher.current();
        }
      },
      { threshold: 1 }
    )
  );
  const [element, setElement] = React.useState(null);
  // Update snapshot of getMembers function whenever page number changes.
  React.useEffect(() => {
    dataFetcher.current = getArchivedProjects;
  }, [getArchivedProjects]);

  // Observe for the load more spinner element
  React.useEffect(() => {
    const currentElement = element;
    const currentObserver = observer.current;
    if (currentElement) {
      currentObserver.observe(currentElement);
    }
    return () => {
      if (currentElement) {
        currentObserver.unobserve(currentElement);
      }
    };
  }, [element]);

  // delete Archived Project
  const deleteProject = (deleteProjectId) => {
    setLoading(true);
    projectService
      .update_project_status({
        teamOwnerId: localStorage.getItem('teamOwnerId'),
        projectId: deleteProjectId,
        status: 'delete',
      })
      .then((response) => {
        if (response.message === 'Success') {
          const updatedList = archivedProjects.filter(
            (projects) => projects._id !== deleteProjectId
          );
          setArchivedProjects([...updatedList]);
          toast.success('Project deleted successfully');
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };
  // Restore Archived Project
  const restoreProject = (restoreProjectId) => {
    setLoading(true);
    projectService
      .update_project_status({
        teamOwnerId: localStorage.getItem('teamOwnerId'),
        projectId: restoreProjectId,
        status: 'active',
      })
      .then((response) => {
        if (response.message === 'Success') {
          const updatedList = archivedProjects.filter(
            (projects) => projects._id !== restoreProjectId
          );
          setArchivedProjects([...updatedList]);
          toast.success('Project restored successfully');
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <>
      <section className="project-list-view">
        {archivedProjects.length > 0
          ? archivedProjects.map((project, index) => {
              // Get the totalTasks count
              const totalTasks = project.tasks.reduce(
                (total, task) => total + task.tasks,
                0
              );
              // Get archived Date
              const archivedDate = project.closedDate;
              const { totalWorkSessionSeconds } = project;
              const totalHours =
                totalWorkSessionSeconds > 0 &&
                convertSecondsToHoursAndMinutes(totalWorkSessionSeconds);

              return (
                <div className="card mt-0" key={index}>
                  <div className="card-body">
                    <Row className="project-title" noGutters>
                      <Col>
                        <>
                          <h4 className="mb-2 name">{project.title}</h4>
                          <p className="small text-muted mb-3">
                            {project.lastWorkSession !== null ? (
                              <time className="small text-muted">
                                last worked{' '}
                                {moment(project.lastWorkSession).fromNow()}
                              </time>
                            ) : (
                              <span className="card-text small text-muted mb-1">
                                {NO_DATA.PROJECT_WORKSESSION_DATA}
                              </span>
                            )}
                          </p>
                        </>
                      </Col>
                    </Row>

                    <div className="row no-gutters task-and-time-info ml-0 mr-0">
                      <div className="col">
                        {archivedDate ? (
                          <>
                            <h6 className="text-uppercase text-muted">
                              Archived date
                            </h6>

                            <h2 className="mb-0">
                              {moment.utc(archivedDate).format('MMM DD,YYYY')}
                            </h2>
                          </>
                        ) : (
                          <div className="col-auto">
                            <span className="badge bg-secondary-soft">
                              Not Available
                            </span>
                          </div>
                        )}
                      </div>
                      <div className="col border-left">
                        <h6 className="text-uppercase text-muted">
                          Total hours
                        </h6>
                        {totalWorkSessionSeconds > 0 ? (
                          <h2 className="mb-0">{totalHours}</h2>
                        ) : (
                          <h2 className="mb-0">0h 0m</h2>
                        )}
                      </div>
                    </div>

                    <div className="row align-items-center">
                      <div className="col">
                        <div className="row align-items-center no-gutters">
                          <h6 className="text-uppercase text-muted">
                            Total tasks
                          </h6>
                          {totalTasks > 0 ? (
                            <h2 className="mb-0">{totalTasks}</h2>
                          ) : (
                            <h2 className="mb-0">0</h2>
                          )}
                        </div>
                      </div>
                      <div className="col-auto ml-auto">
                        <div>
                          {permissions &&
                            permissions.includes('project_restore') && (
                              <Button
                                variant="primary"
                                size="sm"
                                className="ml-auto me-2"
                                onClick={() => restoreProject(project._id)}
                              >
                                Restore
                              </Button>
                            )}
                          {permissions &&
                            permissions.includes('project_delete') && (
                              <DeleteProjectButton
                                projectId={project._id}
                                handleDelete={deleteProject}
                              />
                            )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })
          : !isLoading && (
              <div className="card">
                <div className="card-body d-flex justify-content-center">
                  <div className="text-muted">No archived projects</div>
                </div>
              </div>
            )}
      </section>

      {/* Loading spinner overlay */}
      {isLoading && <Loading contentAreaOnly />}

      {/* Observing button to check if more data available to fetch */}
      {loadMore && (
        <div className="d-flex justify-content-center mb-5">
          <Button
            variant="primary"
            disabled
            ref={setElement}
            className="invisible"
          >
            Loading...
          </Button>
        </div>
      )}
    </>
  );
};
export default ArchivedProject;
